/** Endpoint-only key storage; usable without window/document, including from a service worker. */
import {
  type AgreementWrap,
  accountKeyCommitment,
  type Bytes,
  type ContentBox,
  type ContentContext,
  checkEpochKeys,
  type DirectoryCheckpoint,
  type EcPublicJwk,
  type EpochKey,
  exportP256PublicJwk,
  generateAgreementKeyPair,
  hashCanonicalB64,
  importAccountDataKey,
  jwkThumbprint,
  KeyDirectoryVerifier,
  openContent,
  openUnlockGrant,
  type SignedKeyDirectory,
  type TrustStore,
  type UnlockGrant,
  unwrapAccountKey,
  validId,
  type WrapBinding,
  wrapAccountKey,
} from '@viko/crypto'
import { EncryptedContent } from '@viko/protocol'
import { decryptTranscriptBatch } from './content-pipeline.js'
import { idb } from './idb.js'

export interface ContentIdentity {
  privateKey: CryptoKey
  publicJwk: EcPublicJwk
}
interface VaultRecord {
  transcripts?: Record<string, { head: number; digests: Record<string, string> }>
  identity: ContentIdentity
  checkpoint?: DirectoryCheckpoint
  epochs: Record<string, { key: CryptoKey; wrap: AgreementWrap }>
  /** Authenticated ADK commitment per epoch from every verified snapshot (catch-up/recovery). */
  commitments?: Record<string, string>
  /**
   * Escrow-reader browser (docs/e2e.md "Phone with one Face ID"): it holds epochs through the PRF
   * escrow recipient of this passkey credential instead of its own directory entry. It may seal
   * new content with the current epoch but never provisions other recipients.
   */
  escrowReader?: string
  /**
   * unlock-via-mac: this browser holds the current epoch through a key-holding daemon's verified
   * grant (docs/e2e.md "Unlock via a machine") and has not joined the directory yet. Treated like
   * an escrow reader (may seal with the current epoch, never provisions others) until it joins.
   */
  grantReader?: { machineId: string; epoch: number }
}
/** Where acceptChain finds ADKs to check self-join proofs (docs/e2e.md). */
export interface ProofKeys {
  /** This browser's recipient id (the binding of its stored wraps). */
  recipientId: string
  /** Wraps to this browser from the same catch-up page, not installed yet. */
  pending?: readonly { epoch: number; wrap: AgreementWrap }[]
  /** Another source, e.g. the PRF escrow being opened, or the raw key being published. */
  extra?: (epoch: number) => Promise<Uint8Array | undefined>
}
const storageKey = (accountId: string) => {
  validId(accountId)
  return `e2e/v1/${accountId}`
}

export class ContentVault {
  async ensureIdentity(accountId: string): Promise<ContentIdentity> {
    const key = storageKey(accountId)
    const existing = await idb.get<VaultRecord>(key)
    if (existing) return existing.identity
    const pair = await generateAgreementKeyPair()
    const identity = {
      privateKey: pair.privateKey,
      publicJwk: await exportP256PublicJwk(pair.publicKey),
    }
    // The read/write transaction selects one winner even if page and SW create concurrently.
    const stored = await idb.update<VaultRecord>(
      key,
      (current) => current ?? { identity, epochs: {} },
    )
    return stored.identity
  }
  /** Restore only endpoint-persisted state; server checkpoints are never trust anchors. */
  async directory(accountId: string, trust: TrustStore): Promise<KeyDirectoryVerifier> {
    const state = await idb.get<VaultRecord>(storageKey(accountId))
    if (!state) throw new Error('content locked')
    return new KeyDirectoryVerifier(accountId, trust, state.checkpoint)
  }

  /** Verify outside IDB, then compare-and-swap against the checkpoint and identity we read. */
  async acceptDirectory(
    accountId: string,
    trust: TrustStore,
    input: SignedKeyDirectory,
    now = Date.now(),
    keys?: ProofKeys,
  ): Promise<KeyDirectoryVerifier> {
    return this.acceptChain(accountId, trust, [input], now, keys)
  }
  /** Offline catch-up across revocations: one verified chain, one CAS, commitments recorded. */
  async acceptChain(
    accountId: string,
    trust: TrustStore,
    input: readonly SignedKeyDirectory[],
    now = Date.now(),
    keys?: ProofKeys,
  ): Promise<KeyDirectoryVerifier> {
    const chain = structuredClone(input) as SignedKeyDirectory[]
    const key = storageKey(accountId)
    const before = await idb.get<VaultRecord>(key)
    if (!before) throw new Error('content locked')
    // Key holder: check trusted-browser self-join proofs with the epochs this browser holds (or is
    // about to install from the same page), so it never wraps to an unproven joiner.
    const epochKey = async (epoch: number) => {
      if (!keys) return undefined
      const wraps = [
        ...(before.epochs[String(epoch)] ? [before.epochs[String(epoch)]!.wrap] : []),
        ...(keys.pending ?? []).filter((w) => w.epoch === epoch).map((w) => w.wrap),
      ]
      for (const wrap of wraps)
        try {
          return await unwrapAccountKey(
            wrap,
            { version: 1, accountId, epoch, recipientId: keys.recipientId },
            before.identity.privateKey,
            before.identity.publicJwk,
          )
        } catch {
          // not this epoch's wrap
        }
      return keys.extra?.(epoch)
    }
    const directory = new KeyDirectoryVerifier(accountId, trust, before.checkpoint, { epochKey })
    const accepted = await directory.acceptChain(chain, now)
    await idb.update<VaultRecord>(key, (current) => {
      if (!current || !sameIdentity(current.identity, before.identity))
        throw new Error('content locked')
      if (!sameCheckpoint(current.checkpoint, before.checkpoint))
        throw new Error('directory changed; verify the latest checkpoint before retrying')
      const commitments = { ...current.commitments }
      if (before.checkpoint)
        commitments[before.checkpoint.statement.epoch] = before.checkpoint.statement.keyCommitment
      for (const c of accepted) commitments[c.statement.epoch] = c.statement.keyCommitment
      return { ...current, checkpoint: accepted.at(-1)!, commitments }
    })
    return directory
  }
  /** Commitment for an epoch from verified local state only. */
  private async commitmentFor(accountId: string, epoch: number): Promise<string | undefined> {
    const state = await idb.get<VaultRecord>(storageKey(accountId))
    if (state?.checkpoint?.statement.epoch === epoch)
      return state.checkpoint.statement.keyCommitment
    return state?.commitments?.[String(epoch)]
  }
  /** Install this device's wrap for an epoch it missed while offline (catch-up page). */
  async installEpoch(
    directory: KeyDirectoryVerifier,
    recipientId: string,
    epoch: number,
    wrap: AgreementWrap,
  ): Promise<void> {
    const c = directory.checkpoint
    const r = directory.recipient(recipientId)
    if (!c || r.type !== 'device') throw new Error('unapproved content device')
    const commitment = await this.commitmentFor(directory.accountId, epoch)
    if (!commitment) throw new Error('no verified commitment for epoch')
    await this.installApprovedWrap(
      { version: 1, accountId: directory.accountId, epoch, recipientId },
      wrap,
      r.publicKey,
      commitment,
      c,
    )
  }
  /**
   * Recovery/root transfer: install escrow-recovered epoch keys after checking every key against a
   * commitment from the locally verified chain. Each key is re-wrapped to this browser's own ECDH
   * key so later restarts and provisioning use the ordinary vault path.
   */
  async installRecovered(accountId: string, recipientId: string, keys: readonly EpochKey[]) {
    const key = storageKey(accountId)
    const state = await idb.get<VaultRecord>(key)
    if (!state?.checkpoint) throw new Error('content locked')
    const commitments = new Map<number, string>()
    for (const [epoch, value] of Object.entries(state.commitments ?? {}))
      commitments.set(Number(epoch), value)
    commitments.set(state.checkpoint.statement.epoch, state.checkpoint.statement.keyCommitment)
    await checkEpochKeys(accountId, keys, commitments)
    const installed: Record<string, { key: CryptoKey; wrap: AgreementWrap }> = {}
    for (const k of keys)
      installed[k.epoch] = {
        key: await importAccountDataKey(k.adk),
        wrap: await wrapAccountKey(
          k.adk,
          { version: 1, accountId, epoch: k.epoch, recipientId },
          state.identity.publicJwk,
        ),
      }
    await idb.update<VaultRecord>(key, (current) => {
      if (!current || !sameIdentity(current.identity, state.identity))
        throw new Error('content locked')
      return { ...current, epochs: { ...installed, ...current.epochs } }
    })
  }
  /**
   * unlock-via-mac: install the epoch keys a daemon wrapped to this browser's ECDH key for the
   * request `requestDigest`. Each key is checked against a commitment from the locally verified
   * chain (installRecovered), so a relay or rogue daemon can't substitute a key.
   */
  async installGranted(
    accountId: string,
    recipientId: string,
    grant: UnlockGrant,
    requestDigest: string,
    machineId: string,
  ) {
    const state = await idb.get<VaultRecord>(storageKey(accountId))
    if (!state?.checkpoint) throw new Error('content locked')
    const keys = await openUnlockGrant(grant, {
      accountId,
      deviceKeyId: recipientId,
      requestDigest,
      privateKey: state.identity.privateKey,
      publicJwk: state.identity.publicJwk,
    })
    try {
      // Epochs before this browser's verified history have no commitment here: skip them. The
      // current epoch must be present and match (installRecovered checks every key it installs).
      const known = new Set([
        ...Object.keys(state.commitments ?? {}).map(Number),
        state.checkpoint.statement.epoch,
      ])
      if (!keys.some((k) => k.epoch === state.checkpoint!.statement.epoch))
        throw new Error('The machine did not send your newest key')
      await this.installRecovered(
        accountId,
        recipientId,
        keys.filter((k) => known.has(k.epoch)),
      )
    } finally {
      for (const k of keys) k.adk.fill(0)
    }
    const after = await idb.get<VaultRecord>(storageKey(accountId))
    const head = after?.checkpoint
    if (
      head &&
      after.epochs[String(head.statement.epoch)] &&
      !head.statement.recipients.some((r) => r.id === recipientId)
    )
      await idb.update<VaultRecord>(storageKey(accountId), (current) => {
        if (!current) throw new Error('content locked')
        return { ...current, grantReader: { machineId, epoch: head.statement.epoch } }
      })
  }
  /** Record that this browser reads through `credentialId`'s PRF escrow (no directory join). */
  async markEscrowReader(accountId: string, credentialId: string) {
    validId(credentialId)
    await idb.update<VaultRecord>(storageKey(accountId), (current) => {
      if (!current) throw new Error('content locked')
      return { ...current, escrowReader: credentialId }
    })
  }
  /**
   * Raw keys for every locally held epoch (escrow setup and adding this device after recovery).
   * Each is checked against its verified commitment and zeroed after `use`, even on failure.
   */
  async withEpochKeyMaterial<T>(
    accountId: string,
    recipientId: string,
    use: (keys: EpochKey[]) => Promise<T>,
  ): Promise<T> {
    const state = await idb.get<VaultRecord>(storageKey(accountId))
    if (!state?.checkpoint) throw new Error('content locked')
    const keys: EpochKey[] = []
    try {
      for (const [epoch, entry] of Object.entries(state.epochs)) {
        const n = Number(epoch)
        const adk = await unwrapAccountKey(
          entry.wrap,
          { version: 1, accountId, epoch: n, recipientId },
          state.identity.privateKey,
          state.identity.publicJwk,
        )
        keys.push({ epoch: n, adk })
        const expected =
          n === state.checkpoint.statement.epoch
            ? state.checkpoint.statement.keyCommitment
            : state.commitments?.[epoch]
        if (!expected || (await accountKeyCommitment(adk, accountId, n)) !== expected)
          throw new Error('ADK commitment mismatch')
      }
      return await use(keys.sort((a, b) => a.epoch - b.epoch))
    } finally {
      for (const k of keys) k.adk.fill(0)
    }
  }
  /** Local state for rotation/revocation decisions (never trusted from the relay). */
  async state(accountId: string) {
    const state = await idb.get<VaultRecord>(storageKey(accountId))
    return state
      ? {
          checkpoint: state.checkpoint ? structuredClone(state.checkpoint) : undefined,
          epochs: Object.keys(state.epochs).map(Number),
          identity: state.identity.publicJwk,
          commitments: new Map<number, string>([
            ...Object.entries(state.commitments ?? {}).map(
              ([epoch, value]) => [Number(epoch), value] as [number, string],
            ),
            ...(state.checkpoint
              ? [
                  [state.checkpoint.statement.epoch, state.checkpoint.statement.keyCommitment] as [
                    number,
                    string,
                  ],
                ]
              : []),
          ]),
        }
      : undefined
  }

  async install(
    directory: KeyDirectoryVerifier,
    recipientId: string,
    wrap: AgreementWrap,
  ): Promise<void> {
    const c = directory.checkpoint
    const r = directory.recipient(recipientId)
    if (!c || r.type !== 'device') throw new Error('unapproved content device')
    await this.installApprovedWrap(
      { version: 1, accountId: directory.accountId, epoch: c.statement.epoch, recipientId },
      wrap,
      r.publicKey,
      c.statement.keyCommitment,
      c,
    )
  }
  /** Low-level import; approvedPublicKey MUST come from verified local directory state. */
  private async installApprovedWrap(
    binding: WrapBinding,
    wrap: AgreementWrap,
    approvedPublicKey: EcPublicJwk,
    commitment: string,
    checkpoint: DirectoryCheckpoint,
  ): Promise<void> {
    const key = storageKey(binding.accountId)
    const state = await idb.get<VaultRecord>(key)
    if (!state) throw new Error('content locked')
    if (!sameCheckpoint(state.checkpoint, checkpoint)) throw new Error('directory changed')
    if (
      (await jwkThumbprint(state.identity.publicJwk)) !== (await jwkThumbprint(approvedPublicKey))
    )
      throw new Error('unapproved content key')
    const raw = await unwrapAccountKey(
      wrap,
      binding,
      state.identity.privateKey,
      state.identity.publicJwk,
    )
    try {
      if ((await accountKeyCommitment(raw, binding.accountId, binding.epoch)) !== commitment)
        throw new Error('ADK commitment mismatch')
      const adk = await importAccountDataKey(raw)
      await idb.update<VaultRecord>(key, (current) => {
        if (
          !current ||
          current.identity.publicJwk.x !== state.identity.publicJwk.x ||
          current.identity.publicJwk.y !== state.identity.publicJwk.y
        )
          throw new Error('content locked')
        if (!sameCheckpoint(current.checkpoint, checkpoint)) throw new Error('directory changed')
        const previous = current.epochs[String(binding.epoch)]
        // A newly randomized wrap for the same committed ADK is valid; retain the durable key.
        if (previous) return current
        return { ...current, epochs: { ...current.epochs, [binding.epoch]: { key: adk, wrap } } }
      })
    } finally {
      raw.fill(0)
    }
  }
  /** Only current, approved local recipients may encrypt new data or provision another endpoint. */
  private async approvedCurrent(
    accountId: string,
    trust: TrustStore,
    recipientId: string,
    allowEscrowReader = false,
  ) {
    const state = await idb.get<VaultRecord>(storageKey(accountId))
    if (!state?.checkpoint || state.checkpoint.statement.revocationSeq !== trust.revocationSeq)
      throw new Error('content locked; directory trust synchronization required')
    const directory = new KeyDirectoryVerifier(accountId, trust, state.checkpoint)
    const reader = allowEscrowReader ? state.escrowReader : undefined
    const notListed = !state.checkpoint.statement.recipients.some((r) => r.id === recipientId)
    const own = trust.lookupDevice(recipientId)
    const readerApproved =
      (!!reader &&
        !trust.isCredentialRevoked(reader) &&
        own.status === 'ok' &&
        state.checkpoint.statement.recipients.some((r) => r.id === reader && r.type === 'prf') &&
        notListed) ||
      // unlock-via-mac: a daemon-granted, trusted (non-session) browser that has not joined yet.
      (allowEscrowReader &&
        !!state.grantReader &&
        state.grantReader.epoch === state.checkpoint.statement.epoch &&
        own.status === 'ok' &&
        !own.device.session &&
        notListed)
    if (!readerApproved) {
      const recipient = directory.recipient(recipientId)
      if (
        recipient.type !== 'device' ||
        (await jwkThumbprint(recipient.publicKey)) !==
          (await jwkThumbprint(state.identity.publicJwk))
      )
        throw new Error('unapproved content device')
    }
    const epoch = state.checkpoint.statement.epoch
    const installed = state.epochs[String(epoch)]
    if (!installed) throw new Error('content locked')
    return { state, epoch, installed }
  }
  async currentKey(accountId: string, trust: TrustStore, recipientId: string) {
    const { epoch, installed } = await this.approvedCurrent(accountId, trust, recipientId, true)
    return { epoch, key: installed.key }
  }
  /** Raw material exists only while producing recipient wraps, then is zeroed even on failure. */
  async withCurrentKeyMaterial<T>(
    accountId: string,
    trust: TrustStore,
    recipientId: string,
    use: (raw: Uint8Array) => Promise<T>,
    /** Only for a trusted reader's self-join: the key it holds through its passkey's escrow. */
    opts: { escrowReader?: boolean } = {},
  ): Promise<T> {
    const { state, epoch, installed } = await this.approvedCurrent(
      accountId,
      trust,
      recipientId,
      opts.escrowReader === true,
    )
    const raw = await unwrapAccountKey(
      installed.wrap,
      { version: 1, accountId, epoch, recipientId },
      state.identity.privateKey,
      state.identity.publicJwk,
    )
    try {
      if (
        (await accountKeyCommitment(raw, accountId, epoch)) !==
        state.checkpoint!.statement.keyCommitment
      )
        throw new Error('ADK commitment mismatch')
      return await use(raw)
    } finally {
      raw.fill(0)
    }
  }
  async epochKey(accountId: string, epoch: number): Promise<CryptoKey> {
    const state = await idb.get<VaultRecord>(storageKey(accountId))
    const key = state?.epochs[String(epoch)]?.key
    if (!key) throw new Error('content locked')
    return key
  }
  async open(context: ContentContext, box: ContentBox): Promise<Bytes> {
    return openContent(await this.epochKey(context.accountId, context.epoch), context, box)
  }
  /** Bound concurrency; one IDB lookup per epoch, outside rendering. */
  async openBatch(
    accountId: string,
    objects: readonly { context: ContentContext; box: ContentBox }[],
  ): Promise<Bytes[]> {
    const keys = new Map<number, CryptoKey>()
    for (const o of objects) {
      if (o.context.accountId !== accountId) throw new Error('wrong content account')
      if (!keys.has(o.context.epoch))
        keys.set(o.context.epoch, await this.epochKey(accountId, o.context.epoch))
    }
    const result: Bytes[] = []
    for (let i = 0; i < objects.length; i += 64) {
      result.push(
        ...(await Promise.all(
          objects.slice(i, i + 64).map((o) => {
            const key = keys.get(o.context.epoch)
            if (!key) throw new Error('content locked')
            return openContent(key, o.context, o.box)
          }),
        )),
      )
    }
    return result
  }
  /** Authenticate a contiguous batch, persist replay/fork checkpoints, then release plaintext. */
  async transcriptBatch(
    accountId: string,
    runId: string,
    raw: readonly unknown[],
    minimumEpoch = 1,
  ) {
    if (raw.length > 64) throw new Error('content batch too large')
    const key = storageKey(accountId)
    const before = await idb.get<VaultRecord>(key)
    if (!before) throw new Error('content locked')
    const objects = raw.map((o) => EncryptedContent.parse(o))
    for (let i = 1; i < objects.length; i++) {
      if (objects[i]!.context.seq !== objects[i - 1]!.context.seq + 1)
        throw new Error('transcript sequence gap')
    }
    const events = await decryptTranscriptBatch(this, accountId, runId, objects, minimumEpoch)
    const hashes = await Promise.all(
      objects.map((o) => hashCanonicalB64('viko/transcript-checkpoint/v1', o)),
    )
    await idb.update<VaultRecord>(key, (current) => {
      if (!current || !sameIdentity(current.identity, before.identity))
        throw new Error('content locked')
      const previous =
        current.transcripts && Object.hasOwn(current.transcripts, runId)
          ? current.transcripts[runId]
          : undefined
      const next = { head: previous?.head ?? 0, digests: { ...previous?.digests } }
      for (let i = 0; i < objects.length; i++) {
        const seq = objects[i]!.context.seq
        const hash = hashes[i]!
        if (seq <= next.head) {
          if (next.digests[String(seq)] !== hash) throw new Error('transcript ciphertext conflict')
        } else {
          if (seq !== next.head + 1) throw new Error('transcript sequence gap')
          next.digests[String(seq)] = hash
          next.head = seq
        }
      }
      return { ...current, transcripts: { ...current.transcripts, [runId]: next } }
    })
    return events
  }

  async forget(accountId: string): Promise<void> {
    await idb.del(storageKey(accountId))
  }
}

function sameIdentity(a: ContentIdentity, b: ContentIdentity): boolean {
  return a.publicJwk.x === b.publicJwk.x && a.publicJwk.y === b.publicJwk.y
}
function sameCheckpoint(
  a: DirectoryCheckpoint | undefined,
  b: DirectoryCheckpoint | undefined,
): boolean {
  return a?.revision === b?.revision && a?.digest === b?.digest
}
